import {
  Entity,
  fetchSnakeByName,
  getPreliminaryPrice,
  getPrices,
  PreliminaryPrice,
  PriceHistory,
  postEntity,
  TimeSeriesData,
} from '@api';
import {
  CHARTV2_HORIZON_SPAN_MS,
  ChartV2Horizon,
  ChartV2Interval,
  ChartV2Point,
} from '@chartv2';
import {
  isPriceSnake,
  isSinglePriceEntity,
  mapSnakeSeriesToOhlc,
} from '@helpers';

import {
  PriceChange,
  PriceDisplayProps,
} from '../widgets/price-display/PriceDisplay';

const INTRADAY_INTERVALS: ChartV2Interval[] = ['1m', '5m', '15m', '30m', '1h'];
const NON_INTRADAY_INTERVALS: ChartV2Interval[] = ['1D', '1W', '1M'];

/**
 * Periodicities selectable per horizon — mirrors terminal's
 * `allowedResamplesForHorizon`. Unsupported ones are greyed in the dropdown.
 */
export const INTERVALS_BY_HORIZON: Record<ChartV2Horizon, ChartV2Interval[]> = {
  '1D': INTRADAY_INTERVALS,
  '1W': [...INTRADAY_INTERVALS, '1D'],
  '2W': [...INTRADAY_INTERVALS, '1D'],
  '1M': [...INTRADAY_INTERVALS, '1D'],
  '2M': [...INTRADAY_INTERVALS, '1D'],
  '3M': NON_INTRADAY_INTERVALS,
  '6M': NON_INTRADAY_INTERVALS,
  '1Y': NON_INTRADAY_INTERVALS,
  '3Y': NON_INTRADAY_INTERVALS,
  '5Y': NON_INTRADAY_INTERVALS,
  '10Y': NON_INTRADAY_INTERVALS,
  MAX: NON_INTRADAY_INTERVALS,
};

/**
 * Default periodicity a horizon snaps to when selected — mirrors terminal's
 * `rangeToResampleMapping`.
 */
export const DEFAULT_INTERVAL_BY_HORIZON: Record<
  ChartV2Horizon,
  ChartV2Interval
> = {
  '1D': '1m',
  '1W': '5m',
  '2W': '5m',
  '1M': '1h',
  '2M': '1h',
  '3M': '1D',
  '6M': '1D',
  '1Y': '1D',
  '3Y': '1W',
  '5Y': '1W',
  '10Y': '1M',
  MAX: '1M',
};

/** Horizon the chart opens on. */
export const DEFAULT_HORIZON: ChartV2Horizon = 'MAX';

/**
 * Intervals an entity has no data for — mirrors terminal's
 * `getAssetEmptyResamples`. Snake-only entities only have a daily series
 * (intraday, weekly and monthly are unavailable); non-subscribable price assets
 * have no intraday (sub-daily) data.
 */
export const getEntityEmptyIntervals = (entity: Entity): ChartV2Interval[] => {
  if (isSinglePriceEntity(entity)) {
    return [...INTRADAY_INTERVALS, '1M', '1W'];
  }

  return entity.subscribable_ticker ? [] : INTRADAY_INTERVALS;
};

export const getEnabledIntervals = ({
  entity,
  horizon,
}: {
  entity: Entity;
  horizon: ChartV2Horizon;
}): ChartV2Interval[] => {
  const empty = new Set(getEntityEmptyIntervals(entity));

  return INTERVALS_BY_HORIZON[horizon].filter(interval => !empty.has(interval));
};

/**
 * Snap a horizon/interval pair to one the entity actually has data for. The
 * interval snaps into the horizon's enabled set (preferring the horizon's
 * default); if a horizon has no enabled interval at all (e.g. an intraday-only
 * horizon on a no-intraday asset) it realigns to `DEFAULT_HORIZON` so daily data
 * still shows — horizons are never disabled.
 */
export const resolveResolution = ({
  entity,
  horizon,
  interval,
}: {
  entity: Entity;
  horizon: ChartV2Horizon;
  interval: ChartV2Interval;
}): { horizon: ChartV2Horizon; interval: ChartV2Interval } => {
  let resolvedHorizon = horizon;
  let enabled = getEnabledIntervals({
    entity,
    horizon: resolvedHorizon,
  });

  if (enabled.length === 0) {
    resolvedHorizon = DEFAULT_HORIZON;
    enabled = getEnabledIntervals({
      entity,
      horizon: resolvedHorizon,
    });
  }

  if (enabled.includes(interval)) {
    return { horizon: resolvedHorizon, interval };
  }

  const preferred = DEFAULT_INTERVAL_BY_HORIZON[resolvedHorizon];

  return {
    horizon: resolvedHorizon,
    interval: enabled.includes(preferred) ? preferred : (enabled[0] ?? '1D'),
  };
};

export const resolveHorizonForInterval = ({
  entity,
  horizon,
  interval,
}: {
  entity: Entity;
  horizon: ChartV2Horizon;
  interval: ChartV2Interval;
}): { horizon: ChartV2Horizon; interval: ChartV2Interval } => {
  if (getEnabledIntervals({ entity, horizon }).includes(interval)) {
    return { horizon, interval };
  }

  if (
    getEnabledIntervals({ entity, horizon: DEFAULT_HORIZON }).includes(interval)
  ) {
    return { horizon: DEFAULT_HORIZON, interval };
  }

  return resolveResolution({ entity, horizon, interval });
};

export const pointValue = (point: ChartV2Point): number =>
  'close' in point ? point.close : point.value;

export const entityCurrency = (entity: Entity): string =>
  (entity.currency || 'USD').toUpperCase();

export const snakeIndicatorLabel = (snake: string): string => {
  const field = snake.split('.').pop() ?? '';

  return field ? field.charAt(0).toUpperCase() + field.slice(1) : '';
};

/**
 * Format the change from `baseline` to `current` as a `PriceChange`
 * (signed absolute + percentage). `undefined` when there is no baseline.
 */
export const computePriceChange = (
  current: number,
  baseline: number
): PriceChange | undefined => {
  if (baseline === 0) {
    return undefined;
  }

  const change = current - baseline;
  const proportion = (change / baseline) * 100;
  const sign = change >= 0 ? '+' : '';

  return {
    status: change > 0 ? 'positive' : change < 0 ? 'negative' : 'default',
    change: `${sign}${change.toFixed(2)}`,
    proportionChange: `${sign}${proportion.toFixed(2)}%`,
  };
};

const priceHistoryToPoints = (history: PriceHistory): ChartV2Point[] =>
  history
    .map(bar => ({
      time: Date.parse(bar.time),
      open: bar.open,
      high: bar.high,
      low: bar.low,
      close: bar.close,
    }))
    .filter(point => Number.isFinite(point.time));

const timeseriesToPoints = (
  timeseries: TimeSeriesData,
  snakeName: string
): ChartV2Point[] =>
  mapSnakeSeriesToOhlc({
    data: timeseries.result.data,
    isPrice: isPriceSnake(snakeName),
    parseTime: Date.parse,
  }).filter(point => Number.isFinite(point.time));

export const deriveEntityQuote = (
  entity: Entity,
  points: ChartV2Point[]
): PriceDisplayProps | undefined => {
  if (points.length === 0) {
    return undefined;
  }

  const latest = pointValue(points[points.length - 1]);
  const previous =
    points.length > 1 ? pointValue(points[points.length - 2]) : undefined;

  return {
    price: latest.toFixed(2),
    currency: entityCurrency(entity),
    priceChange:
      previous === undefined ? undefined : computePriceChange(latest, previous),
    showMarketIndicator: false,
  };
};

export const computeHorizonWindow = (
  data: ChartV2Point[],
  horizon: ChartV2Horizon
): { latestValue: number; windowStartValue: number } | undefined => {
  if (data.length === 0) {
    return undefined;
  }

  const latest = data[data.length - 1];
  const span = CHARTV2_HORIZON_SPAN_MS[horizon];
  const startTime = span === null ? data[0].time : latest.time - span;
  const startPoint = data.find(point => point.time >= startTime) ?? data[0];

  return {
    latestValue: pointValue(latest),
    windowStartValue: pointValue(startPoint),
  };
};

interface PriceSeriesRequest {
  entity: Entity;
  basePath: string;
  interval: ChartV2Interval;
  signal: AbortSignal;
}

/** Fetch OHLC price history for a resolution; `[]` on failure/empty. */
const fetchPriceHistory = async ({
  entity,
  basePath,
  interval,
  signal,
}: PriceSeriesRequest): Promise<ChartV2Point[]> => {
  const result = await getPrices(
    {
      ticker: entity.subscribable_ticker,
      entityTag: entity.tag,
      resolution: interval,
    },
    basePath,
    signal
  );

  return result.error || result.validationError || !result.data
    ? []
    : priceHistoryToPoints(result.data);
};

/** Fetch the value-only snake series (fallback / indicators); `[]` on failure. */
const fetchSnakePoints = async (
  entity: Entity,
  signal: AbortSignal
): Promise<ChartV2Point[]> => {
  const result = await fetchSnakeByName(
    { snakeName: entity.default_snake },
    signal
  );

  return result.error || result.validationError || !result.data
    ? []
    : timeseriesToPoints(result.data, entity.default_snake);
};

export interface PriceSeriesResult {
  points: ChartV2Point[];
  /**
   * Resolution the returned points are actually at. Differs from the requested
   * interval when an intraday request finds no data and falls back to daily
   * OHLC, so callers can keep their periodicity control in sync with the data.
   */
  interval: ChartV2Interval;
}

/**
 * Load the entity's price series for a given resolution. Mirrors terminal's
 * turbo-chart branch: tradable price assets use OHLC `getPrices` (with an
 * intraday -> daily retry), everything else — and any failure — falls back to
 * the value-only snake series so the chart is never empty. The returned
 * `interval` reflects the resolution actually fetched (`'1D'` after an
 * intraday -> daily fallback or daily snake fallback).
 */
export const loadEntityPriceSeries = async ({
  entity,
  basePath,
  interval,
  signal,
}: PriceSeriesRequest): Promise<PriceSeriesResult> => {
  if (!isSinglePriceEntity(entity)) {
    const points = await fetchPriceHistory({
      entity,
      basePath,
      interval,
      signal,
    });

    if (points.length > 0) {
      return { points, interval };
    }

    if (INTRADAY_INTERVALS.includes(interval)) {
      const daily = await fetchPriceHistory({
        entity,
        basePath,
        interval: '1D',
        signal,
      });

      if (daily.length > 0) {
        return { points: daily, interval: '1D' };
      }
    }
  }

  const points = await fetchSnakePoints(entity, signal);
  const resolvedInterval =
    points.length > 0 && INTRADAY_INTERVALS.includes(interval)
      ? '1D'
      : interval;

  return { points, interval: resolvedInterval };
};

export const loadPreliminaryPrice = async (
  subscribableTicker: string | undefined,
  basePath: string,
  signal: AbortSignal
): Promise<PreliminaryPrice | undefined> => {
  if (!subscribableTicker) {
    return undefined;
  }

  const result = await getPreliminaryPrice(
    subscribableTicker,
    basePath,
    signal
  );

  return result.error || result.validationError || !result.data
    ? undefined
    : result.data;
};

export type EntityLoadResult =
  | { status: 'aborted' }
  | { status: 'error' }
  | { status: 'ready'; entity: Entity };

/** Fetch entity identity by tag via `POST entity/v2/filtered`. */
export const loadEntity = async (
  entityTag: string,
  signal: AbortSignal
): Promise<EntityLoadResult> => {
  const result = await postEntity(entityTag, signal);

  if (signal.aborted) {
    return { status: 'aborted' };
  }

  if (result.error || result.validationError || !result.data) {
    return { status: 'error' };
  }

  return { status: 'ready', entity: result.data };
};
